import DOMPurify from "dompurify";
import { marked } from "marked";
import React, { useMemo } from "react";
import { NoteFormat } from "@store/canvas";
import Styles from "./styles.module.scss";

type NotePreviewProps = {
	note: string;
	format: NoteFormat;
	/** true: match the app theme; false: show the content with its original look. */
	overrideTheme?: boolean;
};

marked.setOptions({
	gfm: true,
	breaks: true,
});

// Opens links in a new tab so they cannot navigate the preview frame away.
const BASE_TAG = '<base target="_blank">';

// Slim, dark scrollbars matching the app (injected before the note's own CSS).
const SCROLLBAR_CSS =
	"::-webkit-scrollbar{width:8px;height:8px}" +
	"::-webkit-scrollbar-track,::-webkit-scrollbar-corner{background:transparent}" +
	"::-webkit-scrollbar-thumb{background:rgba(255,255,255,.2);border-radius:4px}" +
	"::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,.36)}";

// Light-touch theme override for HTML notes. Runs inside the (sandboxed) preview frame.
// Content that is already dark is left untouched. For light content it only recolours:
//  - light backgrounds -> app colours (page: #14182a, inner boxes: #1c2138)
//  - neutral (grey/black) text -> light text; dark coloured text is lightened, keeping its hue
// Bright colours, buttons, dark backgrounds, borders and media stay as authored.
const THEME_OVERRIDE_SCRIPT = `<script>(function(){
var PAGE='#14182a',BOX='#1c2138',TEXT='rgba(255,255,255,.88)';
function parse(c){var m=/rgba?\\(([^)]+)\\)/.exec(c);if(!m)return null;var p=m[1].split(',').map(parseFloat);return{r:p[0],g:p[1],b:p[2],a:p.length>3?p[3]:1};}
function lum(c){return(.299*c.r+.587*c.g+.114*c.b)/255;}
function neutral(c){return Math.max(c.r,c.g,c.b)-Math.min(c.r,c.g,c.b)<40;}
function apply(){
var root=document.documentElement,body=document.body;if(!body)return;
var els=[root,body].concat([].slice.call(body.querySelectorAll('*')));
var rb=parse(getComputedStyle(root).backgroundColor),bb=parse(getComputedStyle(body).backgroundColor);
var canvas=rb&&rb.a>.5?rb:bb&&bb.a>.5?bb:{r:255,g:255,b:255,a:1};
var lightPage=lum(canvas)>.6;
if(!lightPage)return;
function lighten(c){function f(v){return Math.round(v+(255-v)*.55);}return'rgb('+f(c.r)+','+f(c.g)+','+f(c.b)+')';}
function effLight(el){for(var n=el;n;n=n.parentElement){var b=parse(getComputedStyle(n).backgroundColor);if(b&&b.a>.5)return lum(b)>.6;}return lightPage;}
var jobs=[];
els.forEach(function(el){
var cs=getComputedStyle(el),bg=parse(cs.backgroundColor),fg=parse(cs.color),job={el:el};
if(el===root){if(lightPage)job.bg=PAGE;}
else if(el===body){if(lightPage&&bg&&bg.a>.5)job.bg='transparent';}
else if(bg&&bg.a>.5&&lum(bg)>.6){job.bg=BOX;}
if(fg&&lum(fg)<.5&&effLight(el))job.fg=neutral(fg)?TEXT:lighten(fg);
jobs.push(job);
});
jobs.forEach(function(j){
if(j.bg)j.el.style.setProperty('background-color',j.bg,'important');
if(j.fg)j.el.style.setProperty('color',j.fg,'important');
});
}
if(document.readyState==='complete')apply();else window.addEventListener('load',apply);
})();</script>`;

const THEME_OVERRIDE_STYLE = `<style>${SCROLLBAR_CSS}</style>${THEME_OVERRIDE_SCRIPT}`;

const MARKDOWN_PALETTES = {
	// App theme.
	dark: {
		bg: "#14182a",
		fg: "rgba(255,255,255,.92)",
		border: "rgba(255,255,255,.16)",
		muted: "rgba(255,255,255,.64)",
		link: "#58a6ff",
		codeBg: "rgba(255,255,255,.12)",
		preBg: "rgba(0,0,0,.36)",
		quoteBorder: "rgba(255,255,255,.28)",
		tableBorder: "rgba(255,255,255,.24)",
		stripe: "rgba(255,255,255,.04)",
		th: "rgba(255,255,255,.08)",
		hr: "rgba(255,255,255,.2)",
	},
	// Original GitHub look.
	light: {
		bg: "#fff",
		fg: "#1f2328",
		border: "#d1d9e0",
		muted: "#59636e",
		link: "#0969da",
		codeBg: "rgba(129,139,152,.12)",
		preBg: "#f6f8fa",
		quoteBorder: "#d1d9e0",
		tableBorder: "#d1d9e0",
		stripe: "#f6f8fa",
		th: "transparent",
		hr: "#d1d9e0",
	},
};

// GitHub-style Markdown, themed with the app's dark palette or the original light one.
const buildMarkdownStyle = (overrideTheme: boolean) => {
	const c = MARKDOWN_PALETTES[overrideTheme ? "dark" : "light"];
	return `<style>
${overrideTheme ? SCROLLBAR_CSS : ""}
body{margin:0;padding:16px;background:${c.bg};color:${c.fg};font-size:14px;line-height:1.5;
font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;word-wrap:break-word}
h1,h2,h3,h4,h5,h6{margin:24px 0 16px;font-weight:600;line-height:1.25}
h1,h2{padding-bottom:.3em;border-bottom:1px solid ${c.border}}
h1{font-size:2em}h2{font-size:1.5em}h3{font-size:1.25em}h4{font-size:1em}h5{font-size:.875em}h6{font-size:.85em;color:${c.muted}}
body>:first-child{margin-top:0}
p,ul,ol,blockquote,pre,table{margin:0 0 16px}
ul,ol{padding-left:2em}li+li{margin-top:.25em}
a{color:${c.link};text-decoration:none}a:hover{text-decoration:underline}
code{padding:.2em .4em;font-size:85%;background:${c.codeBg};border-radius:6px;
font-family:ui-monospace,SFMono-Regular,Consolas,"Liberation Mono",Menlo,monospace}
pre{padding:16px;overflow:auto;font-size:85%;line-height:1.45;background:${c.preBg};border-radius:6px}
pre code{padding:0;background:transparent;font-size:100%}
blockquote{padding:0 1em;color:${c.muted};border-left:.25em solid ${c.quoteBorder}}
table{border-collapse:collapse;display:block;width:max-content;max-width:100%;overflow:auto}
th,td{padding:6px 13px;border:1px solid ${c.tableBorder}}
tr:nth-child(2n){background:${c.stripe}}th{font-weight:600;background:${c.th}}
hr{height:.25em;margin:24px 0;padding:0;background:${c.hr};border:0}
img{max-width:100%}
</style>`;
};

const buildDocument = (head: string, body: string) =>
	`<!DOCTYPE html><html><head><meta charset="utf-8">${BASE_TAG}${head}</head><body>${body}</body></html>`;

// A full HTML document is rendered untouched (only the base tag is injected),
// a fragment is wrapped in a bare document - same as a browser / live preview.
const buildHtmlDocument = (html: string, overrideTheme: boolean) => {
	const themeStyle = overrideTheme ? THEME_OVERRIDE_STYLE : "";
	if (/<head[\s>]/i.test(html)) {
		return html.replace(
			/<head([^>]*)>/i,
			`<head$1>${BASE_TAG}${themeStyle}`
		);
	}
	if (/<html[\s>]/i.test(html)) {
		return html.replace(
			/<html([^>]*)>/i,
			`<html$1><head>${BASE_TAG}${themeStyle}</head>`
		);
	}
	return buildDocument(themeStyle, html);
};

const buildMarkdownDocument = (markdown: string, overrideTheme: boolean) =>
	buildDocument(
		buildMarkdownStyle(overrideTheme),
		DOMPurify.sanitize(marked.parse(markdown) as string)
	);

export const NotePreview = ({
	note,
	format,
	overrideTheme = true,
}: NotePreviewProps) => {
	const srcDoc = useMemo(() => {
		if (!note) {
			return "";
		}
		if (format === "html") {
			return buildHtmlDocument(note, overrideTheme);
		}
		if (format === "markdown") {
			return buildMarkdownDocument(note, overrideTheme);
		}
		return "";
	}, [note, format, overrideTheme]);

	if (!note) {
		return (
			<div className={Styles.notePreview}>
				<span className={Styles.previewEmpty}>Nothing to preview</span>
			</div>
		);
	}

	if (format === "text") {
		return (
			<div className={Styles.notePreview}>
				<pre className={Styles.previewPlainText}>{note}</pre>
			</div>
		);
	}

	// Sandboxed frame: the note's own CSS/JS run isolated from the app.
	// allow-same-origin is intentionally omitted so the frame cannot reach the
	// app's DOM, cookies or storage.
	return (
		<div className={`${Styles.notePreview} ${Styles.notePreviewFrame}`}>
			<iframe
				key={overrideTheme ? "themed" : "original"}
				title="Note Preview"
				className={`${Styles.previewFrame} ${
					overrideTheme ? "" : Styles.previewFrameOriginal
				}`}
				srcDoc={srcDoc}
				sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
			/>
		</div>
	);
};
